import { useEffect, useState, FormEvent } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Bed, Bath, Ruler, Calendar, MapPin, Share2, Heart, ChevronLeft, ChevronRight, X, Check } from "lucide-react";
import SiteLayout from "@/components/layout/SiteLayout";
import ContentSection from "@/components/sections/ContentSection";
import SectionHeader from "@/components/sections/SectionHeader";
import StatusBadge from "@/components/sections/StatusBadge";
import CTABlock from "@/components/sections/CTABlock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { sendContactEmails } from "@/lib/contactEmails";
import { cn } from "@/lib/utils";

import heroImg from "@/assets/listing-detail-hero.jpg";
import img1 from "@/assets/listing-detail-1.jpg";
import img2 from "@/assets/listing-detail-2.jpg";
import img3 from "@/assets/listing-detail-3.jpg";
import img4 from "@/assets/listing-detail-4.jpg";

/* ─── Sample listing data (placeholder until real data source) ─── */
const sampleListing = {
  slug: "42-maple-street-lynn-ma",
  status: "for-sale" as const,
  price: "$625,000",
  address: "42 Maple Street",
  city: "Lynn",
  state: "MA",
  zip: "01902",
  beds: 4,
  baths: 2,
  halfBaths: 1,
  sqft: "2,100",
  lotSize: "0.28 acres",
  yearBuilt: "1928",
  garage: "2-car attached",
  propertyType: "Single Family",
  mlsNumber: "MLS-73298451",
  images: [heroImg, img1, img2, img3, img4],
  description: [
    "Welcome to 42 Maple Street, a beautifully renovated colonial nestled on a tree-lined street in one of Lynn's most desirable neighborhoods. This 4-bedroom, 2.5-bath home blends timeless character with modern upgrades, offering over 2,100 square feet of thoughtfully designed living space.",
    "Step inside to discover gleaming hardwood floors, a sun-filled living room with a classic fireplace, and an open-concept kitchen featuring marble countertops, stainless steel appliances, and a large center island. The dining area flows seamlessly to a private backyard, perfect for entertaining.",
    "Upstairs, the spacious primary suite includes a walk-in closet and an updated en-suite bathroom. Three additional bedrooms offer flexibility for family, guests, or a home office. The finished basement provides bonus living space with a full bathroom.",
  ],
  features: [
    "Renovated chef's kitchen with marble counters",
    "Hardwood floors throughout",
    "Central air conditioning",
    "High-efficiency gas heating",
    "Updated electrical and plumbing",
    "New roof (2022)",
    "Finished basement with full bath",
    "Private fenced backyard",
    "Two-car attached garage",
    "Walking distance to parks and schools",
  ],
  neighborhood: {
    walkScore: 78,
    transitScore: 65,
    bikeScore: 72,
    nearbySchools: [
      { name: "Maple Elementary", distance: "0.3 mi", rating: "8/10" },
      { name: "Lynn Classical High School", distance: "1.1 mi", rating: "7/10" },
    ],
    nearbyPlaces: [
      { name: "Lynn Woods Reservation", distance: "0.8 mi" },
      { name: "Central Square Shopping", distance: "0.5 mi" },
      { name: "Lynn Commuter Rail", distance: "1.2 mi" },
    ],
  },
};

/* ─── Lightbox ─── */
const Lightbox = ({
  images,
  index,
  onClose,
  onPrev,
  onNext,
}: {
  images: string[];
  index: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) => (
  <div className="fixed inset-0 z-[100] bg-rich-black/95 flex items-center justify-center" onClick={onClose}>
    <button onClick={onClose} className="absolute top-6 right-6 text-cream/70 hover:text-cream transition-colors" aria-label="Close">
      <X size={28} />
    </button>
    <button
      onClick={(e) => { e.stopPropagation(); onPrev(); }}
      className="absolute left-4 md:left-8 text-cream/70 hover:text-cream transition-colors"
      aria-label="Previous image"
    >
      <ChevronLeft size={36} />
    </button>
    <img
      src={images[index]}
      alt={`Photo ${index + 1}`}
      className="max-h-[85vh] max-w-[90vw] object-contain"
      onClick={(e) => e.stopPropagation()}
    />
    <button
      onClick={(e) => { e.stopPropagation(); onNext(); }}
      className="absolute right-4 md:right-8 text-cream/70 hover:text-cream transition-colors"
      aria-label="Next image"
    >
      <ChevronRight size={36} />
    </button>
    <p className="absolute bottom-6 text-cream/50 font-body text-sm tracking-widest">
      {index + 1} / {images.length}
    </p>
  </div>
);

/* ─── Page Component ─── */
const ListingDetail = () => {
  const { slug } = useParams();
  const listing = sampleListing; // Replace with real data lookup by slug

  const [activeImage, setActiveImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const fullAddress = `${listing.address}, ${listing.city}, ${listing.state}`;
  const [inquiry, setInquiry] = useState({
    name: "",
    email: "",
    phone: "",
    message: `I'm interested in ${fullAddress}. Please contact me with more information.`,
  });
  const [inquiryErrors, setInquiryErrors] = useState<Record<string, string>>({});
  const [inquirySubmitError, setInquirySubmitError] = useState<string | null>(null);
  const [isSubmittingInquiry, setIsSubmittingInquiry] = useState(false);
  const [isInquirySubmitted, setIsInquirySubmitted] = useState(false);

  const validateInquiry = () => {
    const errs: Record<string, string> = {};
    if (!inquiry.name.trim()) errs.name = "Please enter your name.";
    if (!inquiry.email.trim()) errs.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inquiry.email.trim()))
      errs.email = "Please enter a valid email.";
    if (!inquiry.message.trim()) errs.message = "Please include a short message.";
    setInquiryErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleInquirySubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateInquiry()) return;
    setIsSubmittingInquiry(true);
    setInquirySubmitError(null);

    try {
      const sanitized = {
        name: inquiry.name.trim(),
        email: inquiry.email.trim(),
        phone: inquiry.phone.trim() || null,
        interest: "Schedule a Showing",
        preferred_location: fullAddress,
        message: inquiry.message.trim(),
      };

      const id = crypto.randomUUID();
      const { error } = await supabase
        .from("contact_submissions")
        .insert({ ...sanitized, id });
      if (error) throw error;

      await sendContactEmails(id, {
        ...sanitized,
        source: `MA Listing Inquiry — ${fullAddress}`,
      });

      setIsInquirySubmitted(true);
    } catch (err) {
      console.error("Listing inquiry submit error:", err);
      setInquirySubmitError("We couldn't submit your inquiry. Please try again or call (781) 632-3936.");
    } finally {
      setIsSubmittingInquiry(false);
    }
  };


  useEffect(() => {
    document.title = `${listing.address}, ${listing.city}, ${listing.state} | Hadassah Homes`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", `${listing.beds} bed, ${listing.baths} bath home at ${listing.address}, ${listing.city}, ${listing.state}. ${listing.sqft} sqft. Listed at ${listing.price}. View photos, features, and schedule a showing.`);
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    const listingSlug = slug || listing.slug;
    canonical.href = `https://hadassahhomesinc.com/listing/${listingSlug}`;
    const robots = document.querySelector('meta[name="robots"]');
    if (robots) {
      robots.setAttribute("content", "noindex, follow");
    }
  }, [listing, slug]);

  const openLightbox = (i: number) => {
    setLightboxIndex(i);
    setLightboxOpen(true);
  };

  return (
    <SiteLayout>
      {lightboxOpen && (
        <Lightbox
          images={listing.images}
          index={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          onPrev={() => setLightboxIndex((p) => (p === 0 ? listing.images.length - 1 : p - 1))}
          onNext={() => setLightboxIndex((p) => (p === listing.images.length - 1 ? 0 : p + 1))}
        />
      )}

      {/* ── Breadcrumb ── */}
      <div className="bg-secondary on-dark pt-32 md:pt-40 pb-0">
        <div className="mx-auto max-w-wide px-5 md:px-12">
          <nav className="flex items-center gap-2 font-body text-xs uppercase tracking-widest text-secondary-foreground/50">
            <Link to="/listings" className="hover:text-accent transition-colors flex items-center gap-1.5">
              <ArrowLeft size={12} />
              Back to Listings
            </Link>
            <span>/</span>
            <span className="text-secondary-foreground/70">{listing.address}</span>
          </nav>
        </div>
      </div>

      {/* ── Photo Gallery ── */}
      <section className="bg-secondary on-dark pb-12 md:pb-16">
        <div className="mx-auto max-w-wide px-5 md:px-12 pt-6">
          {/* Main image + side grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
            {/* Hero image */}
            <button
              onClick={() => openLightbox(0)}
              className="lg:col-span-2 relative aspect-[16/10] overflow-hidden group cursor-pointer"
            >
              <img
                src={listing.images[activeImage]}
                alt={listing.address}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                width={1920}
                height={1080}
              />
              {listing.status && (
                <div className="absolute top-4 left-4">
                  <StatusBadge variant={listing.status} />
                </div>
              )}
              <div className="absolute inset-0 bg-rich-black/0 group-hover:bg-rich-black/10 transition-colors duration-300" />
            </button>

            {/* Side thumbnails */}
            <div className="hidden lg:grid grid-rows-2 gap-2">
              {listing.images.slice(1, 3).map((img, i) => (
                <button
                  key={i}
                  onClick={() => openLightbox(i + 1)}
                  className="relative overflow-hidden group cursor-pointer"
                >
                  <img src={img} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" width={1280} height={864} />
                  <div className="absolute inset-0 bg-rich-black/0 group-hover:bg-rich-black/10 transition-colors duration-300" />
                </button>
              ))}
            </div>
          </div>

          {/* Thumbnail strip */}
          <div className="flex gap-2 mt-2 overflow-x-auto pb-2">
            {listing.images.map((img, i) => (
              <button
                key={i}
                onClick={() => { setActiveImage(i); openLightbox(i); }}
                className={cn(
                  "shrink-0 w-20 h-14 md:w-24 md:h-16 overflow-hidden border-2 transition-all",
                  activeImage === i ? "border-accent" : "border-transparent hover:border-secondary-foreground/30"
                )}
              >
                <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
            <button
              onClick={() => openLightbox(0)}
              className="shrink-0 w-20 h-14 md:w-24 md:h-16 bg-secondary-foreground/10 flex items-center justify-center font-body text-xs text-secondary-foreground/60 hover:text-accent transition-colors"
            >
              View All
            </button>
          </div>
        </div>
      </section>

      {/* ── Key Details Strip ── */}
      <section className="bg-background border-b border-border">
        <div className="mx-auto max-w-wide px-5 md:px-12 py-8 md:py-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Price + Address */}
            <div>
              <p className="font-headline text-3xl md:text-4xl lg:text-5xl text-foreground">{listing.price}</p>
              <p className="font-body text-muted-foreground mt-1 flex items-center gap-1.5">
                <MapPin size={14} className="text-accent" />
                {listing.address}, {listing.city}, {listing.state} {listing.zip}
              </p>
            </div>

            {/* Specs */}
            <div className="flex flex-wrap gap-6 md:gap-10">
              <div className="flex items-center gap-2.5">
                <Bed size={18} className="text-accent" />
                <div>
                  <p className="font-headline text-xl md:text-2xl text-foreground">{listing.beds}</p>
                  <p className="font-body text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Beds</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <Bath size={18} className="text-accent" />
                <div>
                  <p className="font-headline text-xl md:text-2xl text-foreground">{listing.baths}.{listing.halfBaths}</p>
                  <p className="font-body text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Baths</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <Ruler size={18} className="text-accent" />
                <div>
                  <p className="font-headline text-xl md:text-2xl text-foreground">{listing.sqft}</p>
                  <p className="font-body text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Sqft</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <Calendar size={18} className="text-accent" />
                <div>
                  <p className="font-headline text-xl md:text-2xl text-foreground">{listing.yearBuilt}</p>
                  <p className="font-body text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Built</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button className="p-3 border border-border hover:border-accent hover:text-accent transition-colors text-muted-foreground" aria-label="Save listing">
                <Heart size={18} />
              </button>
              <button className="p-3 border border-border hover:border-accent hover:text-accent transition-colors text-muted-foreground" aria-label="Share listing">
                <Share2 size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Description + Sidebar ── */}
      <ContentSection>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-16">
            {/* Description */}
            <div>
              <h2 className="font-headline text-2xl md:text-3xl text-foreground mb-6">About This Property</h2>
              <div className="space-y-5">
                {listing.description.map((para, i) => (
                  <p key={i} className="font-body text-muted-foreground leading-[1.75]">{para}</p>
                ))}
              </div>
            </div>

            {/* Property Details Grid */}
            <div>
              <h2 className="font-headline text-2xl md:text-3xl text-foreground mb-6">Property Details</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-y-5 gap-x-8 border-t border-border pt-6">
                {[
                  { label: "Property Type", value: listing.propertyType },
                  { label: "Bedrooms", value: String(listing.beds) },
                  { label: "Bathrooms", value: `${listing.baths} full, ${listing.halfBaths} half` },
                  { label: "Square Feet", value: listing.sqft },
                  { label: "Lot Size", value: listing.lotSize },
                  { label: "Year Built", value: listing.yearBuilt },
                  { label: "Garage", value: listing.garage },
                  { label: "MLS #", value: listing.mlsNumber },
                ].map((detail) => (
                  <div key={detail.label}>
                    <p className="font-body text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{detail.label}</p>
                    <p className="font-body text-foreground font-medium">{detail.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Features */}
            <div>
              <h2 className="font-headline text-2xl md:text-3xl text-foreground mb-6">Features & Amenities</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {listing.features.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3 py-2">
                    <Check size={16} className="text-accent mt-0.5 shrink-0" />
                    <span className="font-body text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Neighborhood */}
            <div>
              <h2 className="font-headline text-2xl md:text-3xl text-foreground mb-6">Neighborhood</h2>

              {/* Scores */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                {[
                  { label: "Walk Score", value: listing.neighborhood.walkScore },
                  { label: "Transit Score", value: listing.neighborhood.transitScore },
                  { label: "Bike Score", value: listing.neighborhood.bikeScore },
                ].map((score) => (
                  <div key={score.label} className="border border-border p-5 text-center">
                    <p className="font-headline text-3xl text-accent">{score.value}</p>
                    <p className="font-body text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">{score.label}</p>
                  </div>
                ))}
              </div>

              {/* Schools */}
              <div className="mb-8">
                <h3 className="font-body text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-4">Nearby Schools</h3>
                <div className="space-y-3">
                  {listing.neighborhood.nearbySchools.map((school) => (
                    <div key={school.name} className="flex items-center justify-between py-3 border-b border-border/60">
                      <div>
                        <p className="font-body text-foreground font-medium">{school.name}</p>
                        <p className="font-body text-xs text-muted-foreground">{school.distance}</p>
                      </div>
                      <span className="font-body text-sm text-accent font-semibold">{school.rating}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Nearby places */}
              <div>
                <h3 className="font-body text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-4">Nearby Places</h3>
                <div className="space-y-3">
                  {listing.neighborhood.nearbyPlaces.map((place) => (
                    <div key={place.name} className="flex items-center justify-between py-3 border-b border-border/60">
                      <p className="font-body text-foreground">{place.name}</p>
                      <p className="font-body text-xs text-muted-foreground">{place.distance}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Map placeholder */}
              <div className="mt-8 aspect-[16/9] bg-muted border border-border flex items-center justify-center">
                <div className="text-center">
                  <MapPin size={32} className="text-muted-foreground/40 mx-auto mb-2" />
                  <p className="font-body text-sm text-muted-foreground">Interactive map coming soon</p>
                  <p className="font-body text-xs text-muted-foreground mt-1">{listing.address}, {listing.city}, {listing.state} {listing.zip}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar: Inquiry Form */}
          <div className="lg:col-span-1">
            <div className="sticky top-32 space-y-8">
              {/* Inquiry Card */}
              <div className="border border-border bg-card p-8">
                {isInquirySubmitted ? (
                  <div role="status" aria-live="polite">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center bg-accent text-accent-foreground">
                        <Check className="h-5 w-5" strokeWidth={2.5} />
                      </div>
                      <h3 className="font-headline text-xl text-foreground">Thank you</h3>
                    </div>
                    <p className="mt-4 font-body text-sm leading-relaxed text-muted-foreground">
                      Your showing request has been received. A member of the Hadassah Homes team
                      will reach out within 24 hours. A confirmation has been sent to your inbox.
                    </p>
                  </div>
                ) : (
                  <>
                    <h3 className="font-headline text-xl text-foreground mb-1">Schedule a Showing</h3>
                    <p className="font-body text-sm text-muted-foreground mb-6">Interested in this property? Reach out and we will get back to you within 24 hours.</p>

                    <form className="space-y-4" onSubmit={handleInquirySubmit} noValidate>
                      <div>
                        <label htmlFor="inquiry-name" className="sr-only">Full Name</label>
                        <Input
                          id="inquiry-name"
                          placeholder="Full Name"
                          className="h-12"
                          value={inquiry.name}
                          onChange={(e) => setInquiry({ ...inquiry, name: e.target.value })}
                        />
                        {inquiryErrors.name && <p className="text-destructive text-xs mt-1">{inquiryErrors.name}</p>}
                      </div>
                      <div>
                        <label htmlFor="inquiry-email" className="sr-only">Email</label>
                        <Input
                          id="inquiry-email"
                          type="email"
                          placeholder="Email Address"
                          className="h-12"
                          value={inquiry.email}
                          onChange={(e) => setInquiry({ ...inquiry, email: e.target.value })}
                        />
                        {inquiryErrors.email && <p className="text-destructive text-xs mt-1">{inquiryErrors.email}</p>}
                      </div>
                      <div>
                        <label htmlFor="inquiry-phone" className="sr-only">Phone</label>
                        <Input
                          id="inquiry-phone"
                          type="tel"
                          placeholder="Phone Number"
                          className="h-12"
                          value={inquiry.phone}
                          onChange={(e) => setInquiry({ ...inquiry, phone: e.target.value })}
                        />
                      </div>
                      <div>
                        <label htmlFor="inquiry-message" className="sr-only">Message</label>
                        <Textarea
                          id="inquiry-message"
                          className="min-h-[120px] resize-none"
                          value={inquiry.message}
                          onChange={(e) => setInquiry({ ...inquiry, message: e.target.value })}
                        />
                        {inquiryErrors.message && <p className="text-destructive text-xs mt-1">{inquiryErrors.message}</p>}
                      </div>
                      <Button
                        variant="gold"
                        size="lg"
                        type="submit"
                        className="w-full gap-2"
                        disabled={isSubmittingInquiry}
                      >
                        {isSubmittingInquiry ? "Sending…" : "Request Information"}
                        {!isSubmittingInquiry && <ArrowRight size={14} />}
                      </Button>
                      {inquirySubmitError && (
                        <p role="alert" className="text-destructive text-xs">{inquirySubmitError}</p>
                      )}
                    </form>

                    <p className="font-body text-[10px] text-muted-foreground mt-4 text-center">
                      By submitting, you agree to our{" "}
                      <Link to="/privacy-policy" className="underline hover:text-accent transition-colors">Privacy Policy</Link>.
                    </p>
                  </>
                )}
              </div>

              {/* Agent quick contact */}
              <div className="border border-border bg-card p-8 text-center">
                <p className="font-body text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-3">Prefer to Call?</p>
                <a href="tel:+17816323936" className="font-headline text-xl text-foreground hover:text-accent transition-colors">
                  (781) 632-3936
                </a>
                <p className="font-body text-xs text-muted-foreground mt-2">
                  <a href="mailto:Esther@hadassahhomesinc.com" className="hover:text-accent transition-colors">
                    Esther@hadassahhomesinc.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </ContentSection>

      {/* ── CTA ── */}
      <CTABlock
        title="Ready to See This Home in Person?"
        description="Book a private showing with a Hadassah Homes advisor."
        primaryCta={{ text: "Book a Consultation", href: "/consultation" }}
        variant="dark"
      />
    </SiteLayout>
  );
};

export default ListingDetail;
